import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { PendingUsersTable } from '@/admin/components/PendingUsersTable'
import { usePendingUsers } from '@/admin/hooks'

export const Route = createFileRoute('/admin/pending-users')({
    component: PendingUsersPage,
})

function PendingUsersPage() {
    const [page, setPage] = useState(0)
    const [orderBy, setOrderBy] = useState<'followers' | 'createdAt'>(
        'createdAt'
    )
    const limit = 50

    const { data, isLoading, error } = usePendingUsers({
        limit,
        offset: page * limit,
        orderBy,
    })

    const handleNextPage = () => {
        if (data?.hasMore) {
            setPage((prev) => prev + 1)
        }
    }

    const handlePreviousPage = () => {
        if (page > 0) {
            setPage((prev) => prev - 1)
        }
    }

    const handleOrderByChange = (newOrderBy: 'followers' | 'createdAt') => {
        setOrderBy(newOrderBy)
        setPage(0) // Reset to first page when changing sort
    }

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">
                    Pending Users
                </h1>
                <p className="mt-2 text-gray-600">
                    Review and approve new public account signups
                </p>
            </div>

            {/* Content */}
            {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
                    <div className="mb-2 text-4xl">⚠️</div>
                    <h2 className="mb-2 text-xl font-semibold text-red-900">
                        Error Loading Pending Users
                    </h2>
                    <p className="text-red-700">
                        {error instanceof Error
                            ? error.message
                            : 'Failed to load pending users'}
                    </p>
                </div>
            ) : (
                <PendingUsersTable
                    users={data?.users ?? []}
                    isLoading={isLoading}
                    total={data?.total ?? 0}
                    hasMore={data?.hasMore ?? false}
                    page={page}
                    orderBy={orderBy}
                    onNextPage={handleNextPage}
                    onPreviousPage={handlePreviousPage}
                    onOrderByChange={handleOrderByChange}
                />
            )}
        </div>
    )
}
